// Runtime i18n: the active language, number and text formatting, and DOM helpers. Dictionaries live in en.ts / fa.ts.
import type { SpellDef, SpellStats, StatDelta } from '../game/content.ts';
import type { GearStat } from '../game/gear.ts';
import { en, type CharText, type Dict, type EnemyText, type NameDesc, type StageText, type UiKey } from './en.ts';
import { fa } from './fa.ts';

export type Lang = 'en' | 'fa';

const FA_DIGITS = '۰۱۲۳۴۵۶۷۸۹';
const FA_PERCENT = '٪';

let lang: Lang = 'en';
let dict: Dict = en;
const formatters = new Map<string, Intl.NumberFormat>();

export function setLang(next: Lang): void {
  lang = next;
  dict = next === 'fa' ? fa : en;
}

export function getLang(): Lang {
  return lang;
}

export function num(value: number, options?: Intl.NumberFormatOptions): string {
  const locale = lang === 'fa' ? 'fa-IR' : 'en-US';
  const cacheKey = `${locale}|${JSON.stringify(options ?? null)}`;
  let formatter = formatters.get(cacheKey);
  if (!formatter) {
    formatter = new Intl.NumberFormat(locale, options);
    formatters.set(cacheKey, formatter);
  }
  return formatter.format(value);
}

export function digits(text: string): string {
  return lang === 'fa' ? text.replace(/[0-9]/g, (d) => FA_DIGITS[Number(d)]) : text;
}

// `value` is already in percent units (12 means 12%).
export function percent(value: number, signed = false): string {
  return num(value, signed ? { signDisplay: 'exceptZero' } : undefined) + (lang === 'fa' ? FA_PERCENT : '%');
}

export function t(key: UiKey, params?: Record<string, string | number>): string {
  let text = dict.ui[key] || en.ui[key];
  for (const [name, value] of Object.entries(params ?? {})) {
    text = text.split(`{${name}}`).join(typeof value === 'number' ? num(value) : value);
  }
  return text;
}

const fmtSigned = (value: number) => num(value, { signDisplay: 'exceptZero', maximumFractionDigits: 2 });

// Localised rules.upgradeText: same parts, joined with the language's list separator.
export function formatUpgrade(delta: StatDelta): string {
  const parts: string[] = [];
  for (const key of Object.keys(delta) as Array<keyof SpellStats>) {
    const value = delta[key] ?? 0;
    if (key === 'slow') parts.push(t('upg.slow', { v: fmtSigned(Math.round(value * 100)) }));
    else if (key === 'tick') parts.push(t('upg.tick'));
    else if (key === 'cooldown') parts.push(t('upg.cooldown', { v: fmtSigned(value) }));
    else if (key === 'duration') parts.push(t('upg.duration', { v: fmtSigned(value) }));
    else parts.push(t('upg.add', { v: fmtSigned(value), label: t(`upg.${key}` as UiKey) }));
  }
  return parts.join(t('upg.join'));
}

function lookup<T>(local: Record<string, T>, base: Record<string, T>, id: string): T {
  return local[id] ?? base[id];
}

export function spellName(def: SpellDef, evolved = false): string {
  const text = lookup(dict.spells, en.spells, def.id);
  return evolved ? text.evoName : text.name;
}

export function spellDesc(def: SpellDef, evolved = false): string {
  const text = lookup(dict.spells, en.spells, def.id);
  return evolved ? text.evoDesc : text.desc;
}

export const passiveText = (id: string): NameDesc => lookup(dict.passives, en.passives, id);
export const charText = (id: string): CharText => lookup(dict.characters, en.characters, id);
export const shopText = (id: string): NameDesc => lookup(dict.shop, en.shop, id);
export const enemyText = (id: string): EnemyText => lookup(dict.enemies, en.enemies, id);
export const stageText = (id: string): StageText => lookup(dict.stages, en.stages, id);
export const gearName = (id: string): string => lookup(dict.gear, en.gear, id).name;

// Flat stats keep their unit (HP, armor, HP/s); every other gear stat is a fraction shown as a percent.
const GEAR_DECIMALS: Partial<Record<GearStat, number>> = { maxHp: 0, armor: 1, regen: 2 };

export function formatGearStat(stat: GearStat, value: number): string {
  const flat = GEAR_DECIMALS[stat];
  const digits = flat ?? 1;
  const scale = 10 ** digits;
  return t(`gear.${stat}`, { v: Math.round(value * (flat === undefined ? 100 : 1) * scale) / scale });
}

// Fills every data-i18n* element and the document language, direction and title.
export function applyStatic(): void {
  const root = document.documentElement;
  root.lang = lang;
  root.dir = lang === 'fa' ? 'rtl' : 'ltr';
  document.title = t('doc.title');
  for (const el of document.querySelectorAll<HTMLElement>('[data-i18n]')) el.textContent = t(el.dataset.i18n as UiKey);
  for (const el of document.querySelectorAll<HTMLElement>('[data-i18n-html]')) el.innerHTML = t(el.dataset.i18nHtml as UiKey);
  for (const el of document.querySelectorAll<HTMLElement>('[data-i18n-aria]')) el.setAttribute('aria-label', t(el.dataset.i18nAria as UiKey));
}
